import { PulseBaseProvider } from '../../base-classes';
import { getLocalFireInfoSchema } from '../schemas/fire';
import { FireInfo } from '../types/fire';
import { FeatureCollection } from '@turf/helpers';

const _activeFiresCacheKey = 'activeFires';
const _activeFiresUri='/api/fires/v1';
const _localFireInfoCacheKey = 'localFireInfo';
const _localFireInfoUri = '/api/fires/v1/localfireinfo';
const _firePropertiesCacheKey = 'fireProperties';

interface getActiveFiresSyncParams {
    isCached: boolean;
    cultureInfo: string;
    stdParameters?: {};
}

interface getLocalFireInfoSyncParams {
    latitude: number;
    longitude: number;
    unitParameters: {};
    cultureInfo: string;
    stdParameters?: {};
}

interface getFirePropertiesSyncParams {
    latitude: number;
    longitude: number;
    fireLatitude: number;
    fireLongitude: number;
    unitParameters: {};
    stdParameters?: {};
    cultureInfo: string;
}

export class FireProvider extends PulseBaseProvider {
    constructor(baseUrl: string, authId: string, secret: string, options: any) {
        super(baseUrl, authId, secret, options);
        this.storage.sync[_activeFiresCacheKey] = this.activeFiresSyncer.bind(this);
        this.storage.sync[_localFireInfoCacheKey] = this.localFireInfoSyncer.bind(this);
        this.storage.sync[_firePropertiesCacheKey] = this.firePropertiesSyncer.bind(this);
    }

    async activeFiresSyncer(params): Promise<FeatureCollection> {
        const { id, syncParams, resolve, reject } = params;
        let queryParams = {
            ...syncParams.stdParameters
            ,cachedOnly: syncParams.isCached
            ,verbose: false
        };
        return await super.syncData(syncParams.key, id, queryParams, _activeFiresUri, resolve, reject, null, syncParams.pulseApi);
    }


    async getActiveFires(isCached: boolean|undefined|null, stdParameters: { cultureinfo: string }): Promise<FeatureCollection> {
        const syncParams: getActiveFiresSyncParams = {
            isCached
            , cultureInfo: stdParameters?.cultureinfo
        };
        const id = JSON.stringify(syncParams);

        //Add the standard parameters after generating the ID because they shouldn't be part of the cache key
        syncParams.stdParameters = stdParameters;
        return await super.loadFromStorage(_activeFiresCacheKey, id, syncParams);
    }

    async refreshActiveFires(stdParameters) {
        const key = _activeFiresCacheKey;
        const syncParams = {
            isCached: false
            , cultureInfo: stdParameters?.cultureinfo
        };
        const id =  JSON.stringify(syncParams);
        this.storage.remove({
            key
            ,id
        });

        return await this.getActiveFires(false, stdParameters);
    }

    async localFireInfoSyncer(params): Promise<FireInfo> {
        const { id, syncParams, resolve, reject } = params;
        let queryParams = {
            ...syncParams.stdParameters,
            ...syncParams.unitParameters,
            locationType: 'latitudelongitude',
            location: `${syncParams.latitude},${syncParams.longitude}`,
            verbose: false
        };

        return await super.syncData(syncParams.key, id, queryParams, _localFireInfoUri, resolve, reject, null, syncParams.pulseApi, getLocalFireInfoSchema);
    }

    async getLocalFireInfo(latitude: number, longitude: number, stdParameters: { cultureinfo: string }, unitParameters: {}): Promise<FireInfo> {
        const syncParams: getLocalFireInfoSyncParams = {
            latitude
            ,longitude
            ,unitParameters
            , cultureInfo: stdParameters?.cultureinfo
        };
        const id = JSON.stringify(syncParams);

        //Add the standard parameters after generating the ID because they shouldn't be part of the cache key
        syncParams.stdParameters = stdParameters;

        return await super.loadFromStorage(_localFireInfoCacheKey, id, syncParams);
    }

    async refreshLocalFireInfo(latitude, longitude, stdParameters, unitParameters) {
        const key = _localFireInfoCacheKey;
        const syncParams = {
            latitude
            ,longitude
            ,unitParameters
            , cultureInfo: stdParameters?.cultureinfo
        };
        const id =  JSON.stringify(syncParams);
        this.storage.remove({
            key
            ,id
        });

        return await this.getLocalFireInfo(latitude, longitude, stdParameters, unitParameters);
    }

    async firePropertiesSyncer(params) {
        const { id, syncParams, resolve, reject } = params;
        const { latitude, longitude, fireLatitude, fireLongitude, unitParameters, stdParameters } = syncParams;
        let queryParams = {
            ...stdParameters
            ,...unitParameters
            ,locationType: 'latitudelongitude'
            ,location: `${latitude},${longitude}`
            ,fireLatitude
            ,fireLongitude
            ,includeProperties: true
        };
        return await super.syncData(syncParams.key, id, queryParams, _activeFiresUri, resolve, reject, null, syncParams.pulseApi);
    }

    async getFireProperties(latitude, longitude, fireLatitude, fireLongitude, stdParameters, unitParameters) {
        const syncParams: getFirePropertiesSyncParams = {
            latitude
            ,longitude
            ,fireLatitude
            ,fireLongitude
            ,unitParameters
            , cultureInfo: stdParameters?.cultureinfo
        };
        const id = JSON.stringify(syncParams);

        //Add the standard parameters after generating the ID because they shouldn't be part of the cache key
        syncParams.stdParameters = stdParameters;
        return await super.loadFromStorage(_firePropertiesCacheKey, id, syncParams);
    }
}
